import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Store, ChevronDown, ChevronUp, Boxes, Loader2, CheckCircle2, Package, ExternalLink, Search, Archive, Mail, Copy, Trash2 } from "lucide-react";
import { api, money, etTime, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS = ["new", "processing", "fulfilled", "cancelled"];
const STATUS_LABELS = {
  new: "new", processing: "processing", fulfilled: "fulfilled",
  cancelled: "cancelled", ready_for_pickup: "Ready for Pickup", picked_up: "Picked Up",
};
const statusOptions = (order) =>
  order.fulfillment === "pickup" ? ["new", "processing", "ready_for_pickup", "picked_up", "cancelled"] : STATUS;
const PAYMENT = ["pending", "paid", "unpaid"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [shippoConfigured, setShippoConfigured] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [fulfillFilter, setFulfillFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [bucket, setBucket] = useState("active");

  const load = () => {
    const p = new URLSearchParams();
    p.set("bucket", bucket);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (search.trim()) p.set("search", search.trim());
    if (payFilter) p.set("payment_status", payFilter);
    if (fulfillFilter) p.set("fulfillment", fulfillFilter);
    if (showArchived) p.set("include_archived", "true");
    const qs = p.toString();
    api.get(`/admin/orders${qs ? "?" + qs : ""}`).then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/admin/shippo/status").then((r) => setShippoConfigured(!!r.data.configured)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, payFilter, fulfillFilter, showArchived, bucket]);

  const markPickedUp = async (o) => {
    if (!window.confirm(`Mark ${o.order_number} as picked up? The item(s) will be marked SOLD.`)) return;
    await update(o.order_number, "status", "picked_up");
  };

  const sendPickupNotice = async (o) => {
    try {
      await api.post(`/admin/orders/${o.order_number}/pickup-ready-notice`);
      toast.success(`Pickup-ready notice emailed for ${o.order_number}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send notice");
    }
  };

  const update = async (orderNumber, field, value) => {
    try {
      await api.patch(`/admin/orders/${orderNumber}`, { [field]: value });
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const archiveOrder = async (o) => {
    await update(o.order_number, "archived", !o.archived);
  };

  const resendConfirmation = async (o) => {
    try {
      await api.post(`/admin/orders/${o.order_number}/resend-confirmation`);
      toast.success("Confirmation email resent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteOrder = async (o) => {
    const paid = o.payment_status === "paid";
    const msg = paid
      ? `⚠ ${o.order_number} is a PAID order. Permanently delete it anyway? Archiving is safer.`
      : `Delete test order ${o.order_number}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/orders/${o.order_number}${paid ? "?force=true" : ""}`);
      toast.success("Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyTracking = (num) => {
    navigator.clipboard?.writeText(num);
    toast.success("Tracking number copied");
  };

  const badge = (status) => {
    const map = {
      new: "bg-blue-100 text-blue-700", processing: "bg-amber-100 text-amber-700",
      fulfilled: "bg-green-100 text-green-700", cancelled: "bg-gray-200 text-gray-600",
      ready_for_pickup: "bg-teal-100 text-teal-700", picked_up: "bg-green-100 text-green-700",
      paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", unpaid: "bg-red-100 text-red-700",
    };
    return map[status] || "bg-gray-100 text-gray-600";
  };

  const shipChip = (o) => {
    if (o.fulfillment === "pickup" || o.has_dropship) return null;
    if (o.shippo_label_url || o.shippo_tracking_number)
      return { text: "Labeled", cls: "bg-green-100 text-green-700" };
    if (o.shippo_label_status === "manual_needed")
      return { text: "Label needed (>5lb)", cls: "bg-orange-100 text-orange-700" };
    if (o.shippo_label_status === "failed")
      return { text: "Label failed", cls: "bg-red-100 text-red-700" };
    if (o.payment_status === "paid" && o.status !== "cancelled")
      return { text: "Awaiting label", cls: "bg-amber-100 text-amber-700" };
    return null;
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-4xl text-navy">Orders</h1>
      <p className="text-ink/60 mt-1">Manage customer orders, payments, shipping, and pickups.</p>

      <div className="mt-4 flex gap-2" data-testid="orders-buckets">
        {[["active", "Active Orders"], ["history", "Order History"], ["", "All"]].map(([k, label]) => (
          <button key={k} data-testid={`orders-bucket-${k || "all"}`} onClick={() => setBucket(k)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${bucket === k ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-bold text-navy uppercase">Search
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-navy/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Order #, name, email…" className="block rounded-md border border-navy/20 pl-8 pr-3 py-2 text-sm bg-white font-normal w-56" />
          </div>
        </label>
        <button data-testid="orders-search-btn" onClick={load} className="text-sm font-semibold bg-navy text-white rounded-md px-4 py-2">Search</button>
        <label className="text-xs font-bold text-navy uppercase">Payment
          <select data-testid="orders-filter-payment" value={payFilter} onChange={(e) => setPayFilter(e.target.value)}
            className="block mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white font-normal">
            <option value="">All</option>{PAYMENT.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-navy uppercase">Fulfillment
          <select data-testid="orders-filter-fulfillment" value={fulfillFilter} onChange={(e) => setFulfillFilter(e.target.value)}
            className="block mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white font-normal">
            <option value="">All</option><option value="shipping">shipping</option><option value="pickup">pickup</option>
          </select>
        </label>
        <label className="text-xs font-bold text-navy uppercase">From
          <input type="date" data-testid="orders-filter-start" value={start} onChange={(e) => setStart(e.target.value)}
            className="block mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white font-normal" />
        </label>
        <label className="text-xs font-bold text-navy uppercase">To
          <input type="date" data-testid="orders-filter-end" value={end} onChange={(e) => setEnd(e.target.value)}
            className="block mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white font-normal" />
        </label>
        <label className="flex items-center gap-1.5 text-xs font-bold text-navy uppercase pb-2">
          <input type="checkbox" data-testid="orders-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
        </label>
        {(start || end || search || payFilter || fulfillFilter) && <button data-testid="orders-filter-clear" onClick={() => { setStart(""); setEnd(""); setSearch(""); setPayFilter(""); setFulfillFilter(""); load(); }}
          className="text-sm font-semibold text-brick underline pb-2">Clear</button>}
        <span className="text-xs text-ink/50 pb-2 ml-auto">Newest first · times in US Eastern</span>
      </div>

      {orders.length === 0 ? (
        <p data-testid="admin-orders-empty" className="mt-10 text-navy/50">No orders yet.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {orders.map((o) => (
            <div key={o.order_number} data-testid={`admin-order-${o.order_number}`} className="bg-white rounded-lg border border-navy/15 overflow-hidden">
              <div className="flex items-center gap-4 p-4 flex-wrap">
                <button onClick={() => setExpanded(expanded === o.order_number ? null : o.order_number)} className="text-navy/50">
                  {expanded === o.order_number ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div className="font-display text-lg text-navy">{o.order_number}</div>
                <div className="text-sm text-ink/70">{o.customer.name}</div>
                <div className="text-xs text-ink/40">{etTime(o.created_at)}</div>
                <div className="inline-flex items-center gap-1 text-sm text-ink/60">
                  {o.fulfillment === "pickup" ? <Store className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                  {o.fulfillment}
                </div>
                <div className="font-bold text-brick">{money(o.total)}</div>
                <div className="ml-auto flex items-center gap-2">
                  {o.archived && <span className="text-xs font-bold rounded-full px-2 py-1 bg-gray-300 text-gray-700">Archived</span>}
                  {o.has_dropship && <span className="text-xs font-bold rounded-full px-2 py-1 bg-gold/30 text-brick">Drop-ship</span>}
                  {o.fulfillment === "pickup" && o.payment_status === "paid" && !["picked_up", "cancelled", "fulfilled"].includes(o.status) && (
                    <span data-testid={`awaiting-pickup-chip-${o.order_number}`} className="text-xs font-bold rounded-full px-2 py-1 bg-teal-100 text-teal-700">Paid — Awaiting Pickup</span>
                  )}
                  <span className={`text-xs font-bold rounded-full px-2 py-1 ${badge(o.status)}`}>{STATUS_LABELS[o.status] || o.status}</span>
                  <span className={`text-xs font-bold rounded-full px-2 py-1 ${badge(o.payment_status)}`}>{o.payment_status}</span>
                  {o.fulfillment === "pickup" && o.payment_status === "paid" && !["picked_up", "cancelled", "fulfilled"].includes(o.status) && (
                    <>
                      <button data-testid={`pickup-notice-${o.order_number}`} onClick={() => sendPickupNotice(o)}
                        className="text-xs font-bold rounded-full px-3 py-1 bg-navy text-cream hover:bg-teal-700 transition-colors">
                        {o.pickup_ready_emailed ? "Resend Pickup Notice" : "Send Pickup Ready Notice"}
                      </button>
                      <button data-testid={`mark-picked-up-${o.order_number}`} onClick={() => markPickedUp(o)}
                        className="text-xs font-bold rounded-full px-3 py-1 bg-green-600 text-white hover:bg-green-700 transition-colors">
                        Mark as Picked Up
                      </button>
                    </>
                  )}
                  {shipChip(o) && (
                    <span data-testid={`ship-chip-${o.order_number}`} className={`text-xs font-bold rounded-full px-2 py-1 ${shipChip(o).cls}`}>{shipChip(o).text}</span>
                  )}
                  {o.shippo_tracking_number && (
                    <button data-testid={`copy-tracking-${o.order_number}`} onClick={() => copyTracking(o.shippo_tracking_number)} title="Copy tracking #" className="p-1.5 text-navy hover:text-brick"><Copy className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`resend-confirmation-${o.order_number}`} onClick={() => resendConfirmation(o)} title="Resend confirmation email" className="p-1.5 text-navy hover:text-brick"><Mail className="w-4 h-4" /></button>
                  <button data-testid={`archive-order-${o.order_number}`} onClick={() => archiveOrder(o)} title={o.archived ? "Unarchive" : "Archive"} className="p-1.5 text-navy hover:text-brick"><Archive className="w-4 h-4" /></button>
                  <button data-testid={`delete-order-${o.order_number}`} onClick={() => deleteOrder(o)} title="Delete order" className="p-1.5 text-navy hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  <Link to={`/admin/orders/${o.order_number}`} data-testid={`order-details-link-${o.order_number}`}
                    className="text-xs font-bold rounded-full px-3 py-1 bg-navy text-white hover:bg-brick transition-colors">Details</Link>
                </div>
              </div>

              {expanded === o.order_number && (
                <div className="border-t border-navy/10 p-4 bg-cream/40">
                  {o.has_dropship && o.dropship_items?.length > 0 && (
                    <div data-testid={`dropship-banner-${o.order_number}`} className="mb-4 rounded-lg border border-gold bg-gold/15 p-4">
                      <div className="font-bold text-brick uppercase text-sm">⚠ Drop-ship — order these from your supplier</div>
                      <ul className="mt-2 space-y-1 text-sm text-ink/80">
                        {o.dropship_items.map((d, i) => (
                          <li key={d.cj_vid || `${d.name}-${i}`}>
                            {d.quantity} × <b>{d.name}</b>
                            {d.supplier_name && <span> — {d.supplier_name}</span>}
                            {d.supplier_url && <a href={d.supplier_url} target="_blank" rel="noreferrer" className="text-brick font-semibold ml-1 underline">order here</a>}
                            {d.supplier_cost != null && <span className="text-ink/50"> (cost {money(d.supplier_cost)} ea)</span>}
                          </li>
                        ))}
                      </ul>
                      {o.dropship_items.some((d) => d.cj_vid) && (
                        <CJFulfill order={o} onDone={load} />
                      )}
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-bold text-navy text-sm uppercase">Items</h3>
                      <div className="mt-2 space-y-1">
                        {o.items.map((i) => (
                          <div key={i.product_id} className="flex justify-between text-sm">
                            <span className="text-ink/80">{i.name} × {i.quantity}</span>
                            <span className="font-semibold">{money(i.price * i.quantity)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm pt-2 border-t border-navy/10"><span>Shipping</span><span>{o.shipping_cost === 0 ? "Free" : money(o.shipping_cost)}</span></div>
                        <div className="flex justify-between font-bold text-navy"><span>Total</span><span>{money(o.total)}</span></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-navy text-sm uppercase">Customer</h3>
                      <p className="text-sm text-ink/80 mt-2">{o.customer.email}</p>
                      {o.customer.phone && <p className="text-sm text-ink/80">{o.customer.phone}</p>}
                      {o.shipping_address && (
                        <p className="text-sm text-ink/70 mt-1">
                          {o.shipping_address.line1} {o.shipping_address.line2}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
                        </p>
                      )}
                      {o.note && <p className="text-sm text-ink/70 mt-2 italic">Note: {o.note}</p>}

                      <div className="mt-4 flex flex-wrap gap-3">
                        <div>
                          <label className="block text-xs font-bold text-navy uppercase">Status</label>
                          <select data-testid={`order-status-${o.order_number}`} value={o.status} onChange={(e) => update(o.order_number, "status", e.target.value)} className="mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
                            {statusOptions(o).map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-navy uppercase">Payment</label>
                          <select data-testid={`order-payment-${o.order_number}`} value={o.payment_status} onChange={(e) => update(o.order_number, "payment_status", e.target.value)} className="mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
                            {PAYMENT.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ShippoLabel order={o} configured={shippoConfigured} onDone={load} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CJFulfill({ order, onDone }) {
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState("");
  const [loadingFreight, setLoadingFreight] = useState(false);
  const [sending, setSending] = useState(false);

  if (order.cj_fulfilled) {
    return (
      <div data-testid={`cj-fulfilled-${order.order_number}`} className="mt-3 flex items-center gap-2 text-sm font-semibold text-green-700 border-t border-gold/40 pt-3">
        <CheckCircle2 className="w-4 h-4" /> Sent to CJ {order.cj_order_id ? `(CJ order ${order.cj_order_id})` : ""}
      </div>
    );
  }

  if (order.fulfillment === "pickup") {
    return <div className="mt-3 text-sm text-ink/60 border-t border-gold/40 pt-3">Pickup order — no CJ shipping needed.</div>;
  }

  const getFreight = async () => {
    setLoadingFreight(true);
    try {
      const r = await api.post(`/admin/orders/${order.order_number}/cj-freight`);
      setOptions(r.data.options || []);
      if (r.data.options?.[0]) setSelected(r.data.options[0].logistic_name);
      if (!r.data.options?.length) toast.info("CJ returned no shipping options for this address.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingFreight(false);
    }
  };

  const fulfill = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await api.post(`/admin/orders/${order.order_number}/cj-fulfill`, { logistic_name: selected });
      toast.success("Order sent to CJ Dropshipping!");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 border-t border-gold/40 pt-3" data-testid={`cj-fulfill-${order.order_number}`}>
      <div className="flex items-center gap-2 font-bold text-navy text-sm"><Boxes className="w-4 h-4" /> Auto-fulfill via CJ Dropshipping</div>
      {options === null ? (
        <button data-testid={`cj-get-freight-${order.order_number}`} onClick={getFreight} disabled={loadingFreight}
          className="mt-2 text-sm font-semibold bg-navy text-white rounded-md px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
          {loadingFreight && <Loader2 className="w-4 h-4 animate-spin" />} Get CJ shipping options
        </button>
      ) : (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-bold text-navy uppercase">Shipping method</label>
            <select data-testid={`cj-logistic-${order.order_number}`} value={selected} onChange={(e) => setSelected(e.target.value)}
              className="mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
              {options.map((opt) => (
                <option key={opt.logistic_name} value={opt.logistic_name}>
                  {opt.logistic_name} — {opt.price != null ? money(opt.price) : "?"} {opt.aging ? `(${opt.aging})` : ""}
                </option>
              ))}
            </select>
          </div>
          <button data-testid={`cj-confirm-${order.order_number}`} onClick={fulfill} disabled={sending || !selected}
            className="text-sm font-semibold bg-brick text-white rounded-md px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Confirm &amp; send to CJ
          </button>
        </div>
      )}
    </div>
  );
}

function ShippoLabel({ order, configured, onDone }) {
  const [rates, setRates] = useState(null);
  const [selected, setSelected] = useState("");
  const [loadingRates, setLoadingRates] = useState(false);
  const [buying, setBuying] = useState(false);

  // Labels only apply to self-shipped (non-pickup, non-supplier) orders.
  if (order.fulfillment === "pickup" || order.has_dropship) return null;

  const n = order.order_number;

  if (order.shippo_label_url) {
    return (
      <div data-testid={`shippo-purchased-${n}`} className="mt-6 border-t border-navy/10 pt-4">
        <div className="flex items-center gap-2 font-bold text-green-700 text-sm">
          <CheckCircle2 className="w-4 h-4" /> USPS label purchased
        </div>
        <div className="mt-2 text-sm text-ink/80 space-y-1">
          {order.shippo_carrier && <div>{order.shippo_carrier} {order.shippo_service}</div>}
          {order.shippo_tracking_number && (
            <div data-testid={`shippo-tracking-${n}`}>Tracking: <b>{order.shippo_tracking_number}</b></div>
          )}
          <div className="flex gap-4 pt-1">
            <a href={order.shippo_label_url} target="_blank" rel="noreferrer"
               data-testid={`shippo-download-${n}`}
               className="inline-flex items-center gap-1 text-brick font-semibold underline">
              <ExternalLink className="w-4 h-4" /> Download label (PDF)
            </a>
            {order.shippo_tracking_url && (
              <a href={order.shippo_tracking_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-brick font-semibold underline">
                <ExternalLink className="w-4 h-4" /> Track
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  const notPaid = order.payment_status !== "paid";

  const getRates = async () => {
    setLoadingRates(true);
    try {
      const r = await api.post(`/admin/orders/${n}/shipping-rates`);
      setRates(r.data.rates || []);
      if (r.data.rates?.[0]) setSelected(r.data.rates[0].rate_id);
      if (!r.data.rates?.length) toast.info("Shippo returned no rates for this address.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingRates(false);
    }
  };

  const buy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      await api.post(`/admin/orders/${n}/buy-label`, { rate_id: selected });
      toast.success("USPS label purchased!");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBuying(false);
    }
  };

  return (
    <div data-testid={`shippo-label-${n}`} className="mt-6 border-t border-navy/10 pt-4">
      <div className="flex items-center gap-2 font-bold text-navy text-sm">
        <Package className="w-4 h-4" /> USPS Shipping Label (Shippo)
      </div>
      {notPaid ? (
        <p data-testid={`shippo-awaiting-payment-${n}`} className="mt-2 text-sm text-ink/60">
          Available once the order is paid.
        </p>
      ) : !configured ? (
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <button disabled data-testid={`shippo-buy-disabled-${n}`}
            className="text-sm font-semibold bg-navy/40 text-white rounded-md px-4 py-2 cursor-not-allowed">
            Generate USPS Label
          </button>
          <span data-testid={`shippo-awaiting-key-${n}`}
            className="text-xs font-bold rounded-full px-2 py-1 bg-amber-100 text-amber-700">
            Awaiting Shippo key
          </span>
        </div>
      ) : rates === null ? (
        <button data-testid={`shippo-get-rates-${n}`} onClick={getRates} disabled={loadingRates}
          className="mt-2 text-sm font-semibold bg-navy text-white rounded-md px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
          {loadingRates && <Loader2 className="w-4 h-4 animate-spin" />} Get USPS rates
        </button>
      ) : (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-bold text-navy uppercase">Service &amp; price</label>
            <select data-testid={`shippo-rate-${n}`} value={selected} onChange={(e) => setSelected(e.target.value)}
              className="mt-1 rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
              {rates.map((rt) => (
                <option key={rt.rate_id} value={rt.rate_id}>
                  {rt.carrier} {rt.service} — {money(rt.amount)}{rt.estimated_days ? ` (${rt.estimated_days}d)` : ""}
                </option>
              ))}
            </select>
          </div>
          <button data-testid={`shippo-buy-${n}`} onClick={buy} disabled={buying || !selected}
            className="text-sm font-semibold bg-brick text-white rounded-md px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
            {buying && <Loader2 className="w-4 h-4 animate-spin" />} Buy label
          </button>
        </div>
      )}
    </div>
  );
}
